import { CustomProjectsGateway } from '../../Processes/Gateways/CustomProjects/CustomProjectsGateway';
import { RawParsedFragment } from '@entities/ParsedFragment/Types';
import { ParsedFragmentModel } from '@entities/ParsedFragment/ParsedFragmentModel';
import { ViewItemTypesModeType } from '@modules/Steps/Shared/Enums/ViewItemTypesModeEnum';
import { RawGallery } from '@entities/Gallery/Types';
import { GalleryModel } from '@entities/Gallery/GalleryModel';
import { GalleryGateway } from '../../Processes/Gateways/Gallery/GalleryGateway';
import { useFragmentsStore } from '../../Stores/FragmentsStore';
import { ProjectAddItemPayload } from '../../Processes/Gateways/CustomProjects/Interfaces/ProjectAddItemPayload';
import { ItemEntity } from '@entities/Item/Types';
import { GetItemsTotalPrice } from '@viewModels/FragmentsViewModel/Services/GetItemsTotalPrice';
import { ItemTypeEntity } from '@entities/ItemType/Types';
import { GetGalleryUrlByFragmentId } from '@viewModels/FragmentsViewModel/Services/GetGalleryUrlByFragmentId';
import { useProjectStore } from '../../Stores/ProjectStore';
import { GetFragmentInfoMapForItemTypes } from '@viewModels/FragmentsViewModel/Services/GetFragmentInfoMapForItemTypes';
import { ItemTypeGateway } from '../../Processes/Gateways/ItemTypes/ItemTypesGateway';

const projectGateway = new CustomProjectsGateway();
const galleryGateway = new GalleryGateway();
const itemTypeGateway = new ItemTypeGateway();

export class FragmentsViewModel {
    getParsedFragments = async (projectId: number) => {
        const { setParsedFragments } = useFragmentsStore.getState();
        const templateIndex = 0;
        const response = await projectGateway.getParsedFragments(
            projectId,
            templateIndex
        );

        if (response.error || !response.data) {
            setParsedFragments([]);
            return { error: true, message: 'Failed to get fragments' };
        }

        const fragments = response.data?.map(
            (fragment: RawParsedFragment) => new ParsedFragmentModel(fragment)
        );

        setParsedFragments(fragments);

        return response;
    };

    changeFragmentsViewMode = (viewMode: ViewItemTypesModeType) => {
        const {
            parsedFragments,
            setFragmentsWithMaterialItemTypes,
            setLaborItemTypes,
            setToolItemTypes,
            setFragmentsViewMode,
        } = useFragmentsStore.getState();
        const itemTypes = parsedFragments.flatMap(
            fragment => fragment.itemTypes
        );
        const toolItemTypes = itemTypes.filter(itemType => itemType.tool);
        const laborItemTypes = itemTypes.filter(itemType => itemType.labor);
        const filteredParsedFragments = parsedFragments
            .map(fragment => ({
                ...fragment,
                itemTypes: fragment.itemTypes.filter(
                    itemType => !itemType.tool && !itemType.labor
                ),
            }))
            .filter(fragment => fragment.itemTypes.length > 0);

        setFragmentsViewMode(viewMode);
        setLaborItemTypes(laborItemTypes);
        setToolItemTypes(toolItemTypes);
        setFragmentsWithMaterialItemTypes(filteredParsedFragments);
    };

    getGalleryData = async () => {
        const { setGalleryData } = useFragmentsStore.getState();
        const { parsedFragments } = useFragmentsStore.getState();
        const fragmentIds = parsedFragments.map(fragment => fragment.id);
        const response =
            await galleryGateway.getGalleriesForFragments(fragmentIds);

        if (response.error || !response.data) {
            setGalleryData([]);
            return { error: true, message: 'Failed to get gallery data' };
        }

        const galleryData = response.data?.map(
            (gallery: RawGallery) => new GalleryModel(gallery)
        );

        setGalleryData(galleryData);
        return response;
    };

    getExampleGalleryData = (rawGalleryData: RawGallery[]) => {
        const { setGalleryData } = useFragmentsStore.getState();

        const galleryData = rawGalleryData.map(
            (gallery: RawGallery) => new GalleryModel(gallery)
        );

        setGalleryData(galleryData);
    };

    getItemTypesWithItems = async (projectStoreId: number) => {
        const { parsedFragments, setItemTypesWithItems } =
            useFragmentsStore.getState();

        const itemTypes = parsedFragments.flatMap(
            fragment => fragment.itemTypes
        );

        const itemTypeIds = itemTypes.map(itemType => itemType.id);

        const response = await itemTypeGateway.getItemTypesAll({
            page: 1,
            perPage: 1,
            projectStoreId: projectStoreId ?? 0,
            itemTypeIds,
        });

        if (response.error || !response.data) {
            return { error: true, message: 'Failed to get product types' };
        }

        setItemTypesWithItems(response.data);

        return response;
    };

    getMinimumPriceProjectWithParsedFragments = async (projectId: number) => {
        const { fragmentsViewMode, setIsMinimumPriceLoading } =
            useFragmentsStore.getState();
        setIsMinimumPriceLoading(true);

        try {
            const response =
                await projectGateway.minimumPriceProject(projectId);
            if (response.error || !response.data) {
                return {
                    error: true,
                    message: 'Failed to get minimum price project',
                };
            }

            await this.getParsedFragments(projectId);
            this.changeFragmentsViewMode(fragmentsViewMode);

            return response;
        } finally {
            setIsMinimumPriceLoading(false);
        }
    };

    getFlushProjectWithParsedFragments = async (projectId: number) => {
        const { fragmentsViewMode, setIsResetProjectLoading } =
            useFragmentsStore.getState();
        setIsResetProjectLoading(true);

        try {
            const response = await projectGateway.flushProject(projectId);
            if (response.error || !response.data) {
                return { error: true, message: 'Failed to reset the project' };
            }

            await this.getParsedFragments(projectId);
            this.changeFragmentsViewMode(fragmentsViewMode);
            return response;
        } finally {
            setIsResetProjectLoading(false);
        }
    };

    addItemsToProjectAndGetParsedFragments = async (
        payload: ProjectAddItemPayload[],
        projectId: number
    ) => {
        const response = await projectGateway.addItem(projectId, payload);

        if (response.error || !response.data) {
            return { error: true, message: 'Failed to add products' };
        }
        return await this.getParsedFragments(projectId);
    };

    getFragmentsItemTotal = () => {
        const { parsedFragments } = useFragmentsStore.getState();

        const allItemTypes = parsedFragments.flatMap(
            fragment => fragment.itemTypes
        );

        return allItemTypes.reduce((total, itemType) => {
            if (itemType.items && itemType.items.length > 0) {
                return total + GetItemsTotalPrice(itemType.items);
            } else if (itemType.fairMarketPrice != null) {
                const price = parseFloat(itemType.fairMarketPrice);
                const qty = itemType.qty ?? 0;

                if (!isNaN(price) && qty > 0) {
                    return total + qty * price;
                }
            }

            return total;
        }, 0);
    };

    getFragmentsToolItemTotal = () => {
        const { parsedFragments } = useFragmentsStore.getState();

        const toolItemTypes = parsedFragments
            .flatMap(fragment => fragment.itemTypes)
            .filter(itemType => itemType.tool);

        return toolItemTypes.reduce((total, itemType) => {
            if (itemType.items && itemType.items.length > 0) {
                return total + GetItemsTotalPrice(itemType.items);
            } else if (itemType.fairMarketPrice != null) {
                const price = parseFloat(itemType.fairMarketPrice);
                const qty = itemType.qty ?? 0;

                if (!isNaN(price) && qty > 0) {
                    return total + qty * price;
                }
            }

            return total;
        }, 0);
    };

    getFragmentsMaterialsItemTotal = () => {
        const { parsedFragments } = useFragmentsStore.getState();

        const materialItemTypes = parsedFragments
            .flatMap(fragment => fragment.itemTypes)
            .filter(itemType => !itemType.tool && !itemType.labor);

        return materialItemTypes.reduce((total, itemType) => {
            if (itemType.items && itemType.items.length > 0) {
                return total + GetItemsTotalPrice(itemType.items);
            } else if (itemType.fairMarketPrice != null) {
                const price = parseFloat(itemType.fairMarketPrice);
                const qty = itemType.qty ?? 0;

                if (!isNaN(price) && qty > 0) {
                    return total + qty * price;
                }
            }

            return total;
        }, 0);
    };

    getFragmentsLabourItemTotal = () => {
        const { parsedFragments } = useFragmentsStore.getState();

        const laborItemTypes = parsedFragments
            .flatMap(fragment => fragment.itemTypes)
            .filter(itemType => itemType.labor);

        return laborItemTypes.reduce((total, itemType) => {
            if (itemType.items && itemType.items.length > 0) {
                return total + GetItemsTotalPrice(itemType.items);
            } else if (itemType.fairMarketPrice != null) {
                const price = parseFloat(itemType.fairMarketPrice);
                const qty = itemType.qty ?? 0;

                if (!isNaN(price) && qty > 0) {
                    return total + qty * price;
                }
            }

            return total;
        }, 0);
    };

    getItemTypesTotal = (itemTypes: ItemTypeEntity[]) => {
        return itemTypes.reduce((total, itemType) => {
            if (itemType.items && itemType.items.length > 0) {
                return total + GetItemsTotalPrice(itemType.items);
            } else if (itemType.fairMarketPrice != null) {
                const price = parseFloat(itemType.fairMarketPrice);
                const qty = itemType.qty ?? 0;

                if (!isNaN(price) && qty > 0) {
                    return total + qty * price;
                }
            }

            return total;
        }, 0);
    };

    getTotalForLaborOrToolsItemTypes = (itemTypes: ItemTypeEntity[]) => {
        return itemTypes.reduce((total, itemType) => {
            if (itemType.items && itemType.items.length > 0) {
                return total + GetItemsTotalPrice(itemType.items);
            } else if (itemType.fairMarketPrice != null) {
                const price = parseFloat(itemType.fairMarketPrice);
                const qty = itemType.qty ?? 0;

                if (!isNaN(price) && qty > 0) {
                    return total + qty * price;
                }
            }

            return total;
        }, 0);
    };

    getFragmentGallery = (fragmentId: number): string => {
        const { galleryData } = useFragmentsStore.getState();
        return GetGalleryUrlByFragmentId(fragmentId, galleryData);
    };

    updateParsedFragment = async (fragmentUuid: string) => {
        const { parsedFragments, setIsFragmentsUpdateLoading } =
            useFragmentsStore.getState();
        const { customProject } = useProjectStore.getState();

        const currentFragment = parsedFragments.find(
            fragment => fragment.uuid === fragmentUuid
        );

        if (!currentFragment) {
            return false;
        }

        setIsFragmentsUpdateLoading(true);
        try {
            const updatedItemTypes = currentFragment.itemTypes.filter(
                itemType => itemType.items && itemType.items.length > 0
            );

            const payload: ProjectAddItemPayload[] = updatedItemTypes
                .map(item => item.getAddItemPayload())
                .map(payload => ({
                    ...payload,
                    fragment_id: currentFragment.id,
                    fragment_index: currentFragment.index,
                    template_id: customProject.templates
                        ? customProject.templates[0].id
                        : 0,
                    template_index: 0,
                }));

            return await this.addItemsToProjectAndGetParsedFragments(
                payload,
                customProject.id
            );
        } finally {
            setIsFragmentsUpdateLoading(false);
        }
    };

    updateProjectParsedFragments = async () => {
        const { parsedFragments, setIsFragmentsUpdateLoading } =
            useFragmentsStore.getState();
        const { customProject } = useProjectStore.getState();

        setIsFragmentsUpdateLoading(true);

        try {
            const fragmentsPayload = parsedFragments.flatMap(fragment =>
                fragment.getAddItemPayload()
            );

            const payload: ProjectAddItemPayload[] = fragmentsPayload.map(
                item => ({
                    ...item,
                    template_id: customProject.templates
                        ? customProject.templates[0].id
                        : 0,
                    template_index: 0,
                })
            );

            return await this.addItemsToProjectAndGetParsedFragments(
                payload,
                customProject.id
            );
        } finally {
            setIsFragmentsUpdateLoading(false);
        }
    };

    updateLaborOrToolsItemTypes = async (itemTypes: ItemTypeEntity[]) => {
        const { customProject } = useProjectStore.getState();
        const { parsedFragments, setIsFragmentsUpdateLoading } =
            useFragmentsStore.getState();

        setIsFragmentsUpdateLoading(true);
        try {
            const fragmentsInfo =
                GetFragmentInfoMapForItemTypes(parsedFragments);

            const updatedItemTypes = itemTypes.filter(
                itemType => itemType.items && itemType.items.length > 0
            );
            const payload: ProjectAddItemPayload[] = updatedItemTypes.map(
                item => {
                    const fragmentInfo = fragmentsInfo.get(item.uuid);

                    return {
                        ...item.getAddItemPayload(),
                        fragment_id: fragmentInfo?.fragment_id ?? 0,
                        fragment_index: fragmentInfo?.fragment_index ?? 0,
                        template_id: customProject.templates?.[0].id ?? 0,
                        template_index: 0,
                    };
                }
            );

            return await this.addItemsToProjectAndGetParsedFragments(
                payload,
                customProject.id
            );
        } finally {
            setIsFragmentsUpdateLoading(false);
        }
    };

    // updateItemTypesItems = (itemType: ItemTypeEntity) => {
    //     const {
    //         parsedFragments,
    //         toolItemTypes,
    //         laborItemTypes,
    //         fragmentsWithMaterialItemTypes, setFragmentsWithMaterialItemTypes, setLaborItemTypes, setToolItemTypes, setParsedFragments
    //     } = useFragmentsStore.getState();
    //
    //     const { updateItemTypesItems } = useItemsViewModel.getState();
    //     updateItemTypesItems(itemType);
    //
    //     setFragmentsWithMaterialItemTypes(fragmentsWithMaterialItemTypes),
    //
    // };
}
